
import { Bot, Sprout, Leaf } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { checkChatbotReportFunction, sendManualChatbotReport } from "@/components/membership/utils/emailService";
import { useState } from "react";

const SpudBot = () => {
  const navigate = useNavigate();
  const [isSending, setIsSending] = useState(false);

  const handleNavigate = () => {
    navigate('/chatbot');
    window.scrollTo(0, 0);
  };

  const handleTriggerReport = async () => {
    setIsSending(true);
    try {
      // First check if the function is accessible
      const check = await checkChatbotReportFunction();
      
      if (!check.success) {
        toast({
          title: "Edge Function Error",
          description: check.message || "The SpudBot report function is not accessible",
          variant: "destructive",
        });
        setIsSending(false);
        return;
      }
      
      // If accessible, try to send the report
      const result = await sendManualChatbotReport();
      
      if (result.success) {
        toast({
          title: "Report Triggered",
          description: "SpudBot weekly report has been sent successfully",
        });
      } else {
        toast({
          title: "Report Error",
          description: "Failed to send SpudBot report. Check console for details.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error triggering report:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  // Check for admin role - this is a simple check
  // In a real app, you'd check user permissions
  const isAdmin = window.location.pathname.includes('/admin');

  return (
    <section id="spudbot" className="py-8 bg-gradient-to-b from-muted to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-8">
          <div className="flex items-center justify-center mb-6 space-x-2 px-2">
            <Bot className="h-8 w-8 md:h-10 md:w-10 text-primary animate-bounce shrink-0" />
            <h2 className="text-3xl md:text-4xl font-bold text-primary text-center break-words">SpudBot Assistant</h2>
          </div>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-8">
            <div className="flex items-center space-x-2 text-accent">
              <Sprout className="h-5 w-5 shrink-0" />
              <span>Seed Potato Insights</span>
            </div>
            <div className="hidden md:block h-4 w-px bg-border" />
            <div className="flex items-center space-x-2 text-accent">
              <Leaf className="h-5 w-5 shrink-0" />
              <span>Agricultural Expertise</span>
            </div>
          </div>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8 px-2">
            Get instant answers to your seed potato questions from our intelligent assistant. 
            SpudBot combines agricultural expertise with advanced AI to provide accurate, 
            helpful information about seed potato farming and the Seed Potato Organisation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90"
              onClick={handleNavigate}
            >
              Open SpudBot Assistant
              <Bot className="ml-2 h-5 w-5" />
            </Button>
            
            {isAdmin && (
              <Button 
                size="lg" 
                variant="outline"
                onClick={handleTriggerReport}
                disabled={isSending}
              >
                {isSending ? "Sending..." : "Trigger Weekly Report"}
                <Bot className="ml-2 h-5 w-5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpudBot;
